import {
  Autocomplete,
  Checkbox,
  Chip,
  Tooltip,
  Grid,
  Button,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Box,
  AdapterDateFns,
  MuiDatePickers,
  AlertTypes,
  setAlert,

  MuiAlert,
  AlertTitle,
} from '@leap4/frontend';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { convertArrayToMap } from '../../../../utils/functions';
import { AXIOS_INSTANCE } from '../../../../api/axiosInstance';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { ReportContext } from '../reportContext';
import {
  ADVERTISER_EVENT_METRICS,
  eventMetricKey,
  eventNameFromMatchedEventMetricKey,
  eventNameFromMetricKey,
  matchedEventMetricKey,
  eventNameFromPredictedMetricKey,
  isPredictedMetricKey,
  PREDICTION_ANCHOR_METRIC,
  predictedMetricKey,
  sumEventMetricKey,
  getDateDimensionLabel,
  getFilterDimensionOptionsForMetrics,
  getDimensionOptionsForMetrics,
  isEventMetricKey,
  isMatchedEventMetricKey,
  isSumEventMetricKey,
  eventNameFromSumEventMetricKey,
  getTimeFromAggregationPresets,
  PIXEL_DATE_METRICS,
  estimateReportSpeed,
  fastDimensionKeys,
  timeOptions,
  timePresets,
  rawPixelOnlyRangeError,
} from '../reports';
import Constraints from './constraints';
import Filters from './filters';
import ReportChart from '../chart/reportChart';
import { usePredictionSeries } from '../chart/usePredictionSeries';
import { ReportTable } from './reportTable';
import { metricColumnName } from '../grid/reportColumns';

const createRowId = (prefix: string): string =>
  globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const createFilterId = (): string => createRowId('filter');
const createConstraintId = (): string => createRowId('constraint');

const { DatePicker, LocalizationProvider } = MuiDatePickers;
interface IMetricOption {
  key: string;
  label: string;
  hint?: string;
}

interface ICampaignCalcMetric {
  key: string;
  display_label: string;
  target_mode: string;
  is_active?: boolean;
  formula?: { tokens: Array<{ t: string; kind?: string; key?: string }> };
}

const calcMetricFamily = (m: ICampaignCalcMetric): 'pixel' | 'advertiser' =>
  (m.formula?.tokens || []).some(
    (t) =>
      t.t === 'ref' &&
      ((t.kind === 'metric' && t.key === 'advertiser_event_count') || t.kind === 'sum_event')
  )
    ? 'advertiser'
    : 'pixel';

const ReportBuilderView = () => {
  const {
    report,
    setReport,
    showReportSettings,
    columns,
    reportData,
    chartRows,
    runReport,
    isFetchingReportData,
    reportError,
    metadata,
    validationErrors,
    queryStatus,
    queryElapsedMs,
    cancelQuery,
    setTabIndex,
  } = useContext(ReportContext);

  // Live, not on submit: the range and the metric can be chosen in either order, and either one
  // is what makes the pair invalid.
  const rangeError = useMemo(
    () => rawPixelOnlyRangeError(report.metrics, report.startDate, report.endDate),
    [report.metrics, report.startDate, report.endDate],
  );

  // Taken from the selected metrics in selection order, not from column order: the series is fetched
  // for one event and the chart must draw that same event's column, or the two disagree and the
  // forecast silently disappears.
  const predictedEventsInResult = useMemo(
    () =>
      Array.from(
        new Set(
          report.metrics
            .map((m) => m.eventType)
            .filter(isPredictedMetricKey)
            .map(eventNameFromPredictedMetricKey)
            .filter(Boolean)
        )
      ),
    [report.metrics]
  );
  // Every selected predicted metric is fetched and drawn. Each series carries its own event, so the
  // chart no longer has to guess which one a column belongs to.
  // idCampaign holds one id or several joined by commas, the same contract the backend and External use.
  const selectedCampaigns = useMemo(
    () => Array.from(new Set((report.idCampaign || '').split(',').map((c) => c.trim()).filter(Boolean))),
    [report.idCampaign]
  );
  const multiCampaign = selectedCampaigns.length > 1;
  // Predictions are fitted per campaign, so a multi-campaign report has none.
  const { series: predictionSeries, error: predictionError } = usePredictionSeries(
    multiCampaign ? undefined : report.idCampaign,
    multiCampaign ? [] : predictedEventsInResult
  );

  const dispatch = useAppDispatch();

  const selectedOrganizationId = useAppSelector((state) => state.auth.selectedOrganizationId);

  // reportError covers every failure that arrived with a reason; the status check is the
  // backstop for one that did not, so the UI never goes silent about a query that failed.
  const hasReportFailure = !!reportError || queryStatus === 'failed';

  const [campaignOptions, setCampaignOptions] = useState<{ id: string; name: string }[]>([]);
  const [eventOptions, setEventOptions] = useState<
    { key: string; value: string; isAttributed: boolean; isPredicted: boolean }[]
  >([]);
  const [campaignCalculatedMetrics, setCampaignCalculatedMetrics] = useState<ICampaignCalcMetric[]>([]);
  const customLabelMap = useMemo(
    () => new Map((report.customLabels || []).map((item) => [item.key, item.value])),
    [report.customLabels]
  );

  useEffect(() => {
    let cancelled = false;
    AXIOS_INSTANCE.get('/campaigns', { params: { scope: 'organization' } })
      .then((res) => {
        if (cancelled) return;
        const campaigns = (res.data.data || []).map((c: any) => ({
          id: c.id_campaign,
          name: c.name || c.id_campaign,
        }));
        setCampaignOptions(campaigns);
      })
      .catch(() => {
        if (!cancelled) setCampaignOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (selectedCampaigns.length === 0 || campaignOptions.length === 0) return;
    const known = selectedCampaigns.filter((id) => campaignOptions.some((c) => c.id === id));
    if (known.length !== selectedCampaigns.length) {
      setReport({ ...report, idCampaign: known.join(',') });
    }
  }, [campaignOptions, selectedCampaigns, report, setReport]);

  useEffect(() => {
    if (!report.idCampaign) {
      setEventOptions([]);
      setCampaignCalculatedMetrics([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(selectedCampaigns.map((id) => AXIOS_INSTANCE.get(`/campaigns/${encodeURIComponent(id)}/reporting-config`)))
      .then((responses) => {
        if (cancelled) return;
        // Config is per campaign; several campaigns offer the union of their events, and no calculated or predicted metrics.
        const config =
          responses.length === 1
            ? responses[0].data || {}
            : {
                events: Array.from(
                  new Map(
                    responses
                      .flatMap((r: any) => r?.data?.events || [])
                      .map((e: any) => [String(e.event || '').toLowerCase(), { ...e, is_predicted: false }])
                  ).values()
                ),
                calculated_metrics: [],
                metric_labels: [],
              };
        const options = (config.events || [])
          .filter((event: any) => event?.is_active !== false)
          .map((event: any) => ({
            key: String(event.event || '').toLowerCase(),
            value: event.display_label || event.event,

            isAttributed: event.is_attributed !== false,
            isPredicted: event.is_predicted === true,
          }));
        setEventOptions(options);

        const calcMetrics: ICampaignCalcMetric[] = (config.calculated_metrics || [])
          .map((c: any) => ({
            key: String(c.key || '').trim(),
            display_label: String(c.display_label || c.key || '').trim(),
            target_mode: c.target_mode,
            is_active: c.is_active !== false,
            formula: c.formula,
          }))
          .filter((c: ICampaignCalcMetric) => c.key.length > 0);
        setCampaignCalculatedMetrics(calcMetrics);

        const calcLabels = calcMetrics
          .filter((c) => c.target_mode === 'new' && c.is_active)
          .map((c) => ({ key: c.key, value: c.display_label }))
          .filter((c) => c.value.length > 0);
        const metricLabels = (config.metric_labels || [])
          .map((item: any) => ({
            key: String(item.metric_key || '').trim(),
            value: String(item.display_label || '').trim(),
          }))
          .filter((item: any) => item.key.length > 0 && item.value.length > 0);
        const labelByKey = new Map<string, string>();
        for (const l of [...metricLabels, ...calcLabels]) labelByKey.set(l.key, l.value);
        const nextCustomLabels = Array.from(labelByKey, ([key, value]) => ({ key, value }));

        const allowed = new Set(options.map((option: any) => option.key));
        const availableCalcKeys = new Set(
          calcMetrics.filter((c) => c.target_mode === 'new' && c.is_active).map((c) => c.key)
        );
        const prunedMetrics = report.metrics.filter((metric) => {
          if (isEventMetricKey(metric.eventType)) {
            const selectedEvent = eventNameFromMetricKey(metric.eventType);
            return allowed.has(selectedEvent);
          }
          if (isMatchedEventMetricKey(metric.eventType)) {
            const selectedEvent = eventNameFromMatchedEventMetricKey(metric.eventType);
            return allowed.has(selectedEvent);
          }
          if (isSumEventMetricKey(metric.eventType)) {
            const selectedEvent = eventNameFromSumEventMetricKey(metric.eventType);
            return allowed.has(selectedEvent);
          }
          if (isPredictedMetricKey(metric.eventType)) {
            const selectedEvent = eventNameFromPredictedMetricKey(metric.eventType);
            return allowed.has(selectedEvent);
          }

          // Read from the catalogue rather than a hardcoded list, which pruned any core metric the
          // backend added a second after it was picked. Held until the catalogue has loaded: an
          // empty set here would strip every core metric out of the report instead.
          if (!metadata.loaded) return true;
          if (metadata.metrics.some((m) => m.key === metric.eventType)) return true;
          return availableCalcKeys.has(metric.eventType);
        });
        const shouldUpdateMetrics = prunedMetrics.length !== report.metrics.length;
        const currentLabelSig = JSON.stringify(report.customLabels || []);
        const nextLabelSig = JSON.stringify(nextCustomLabels);
        if (shouldUpdateMetrics || currentLabelSig !== nextLabelSig) {
          setReport({
            ...report,
            metrics: prunedMetrics,
            customLabels: nextCustomLabels,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setEventOptions([]);
      });
    return () => {
      cancelled = true;
    };
    // metadata gates the prune above, so it has to re-run once the catalogue arrives.
  }, [report.idCampaign, selectedCampaigns, report, setReport, metadata.loaded, metadata.metrics]);

  const metricsOptionsForComponent = useMemo<IMetricOption[]>(() => {

    const family = report.reportType === 'advertiser' ? 'conversion' : 'impression';
    const baseMetrics = metadata.metrics
      .filter((m) => m.selectable !== false && (m.reports ?? []).includes(family))
      .map((m) => ({
        key: m.key,
        label: customLabelMap.get(m.key) || m.label,
        hint: m.hint,
      }));

    const isAmountEvent = (slug: string): boolean => {
      const s = String(slug || '').toLowerCase();
      return s.endsWith('sum') || s.endsWith('commission');
    };
    const isLastTouchEvent = (key: string): boolean =>
      String(key || '').toLowerCase().endsWith('_last_touch');
    const eventMetrics = eventOptions
      .filter((event) => report.reportType !== 'advertiser' || !isAmountEvent(event.key))
      // Last-touch events are derived from attribution rows, not from adte_conversion_v2, so they
      // belong to neither the attributed list nor the advertiser one. They get their own below.
      .filter((event) => !isLastTouchEvent(event.key))
      .filter((event) => report.reportType === 'advertiser' || event.isAttributed)
      .map((event) => ({
        key: eventMetricKey(event.key),
        label: report.reportType === 'advertiser' ? event.value : `${event.value} (Attributed)`,
        hint: report.reportType === 'advertiser' ? 'raw advertiser events' : 'attributed amount',
      }));

    const matchedEventMetrics =
      report.reportType === 'advertiser'
        ? []
        : eventOptions
            .filter((event) => !isAmountEvent(event.key))

            .filter((event) => event.isAttributed)
            .map((event) => ({
              key: matchedEventMetricKey(event.key),
              label: `Matched Impression (${event.value})`,
              hint: 'raw matched impressions',
            }));
    const sumEventMetrics =
      report.reportType === 'advertiser'
        ? eventOptions
            .filter((event) => isAmountEvent(event.key))
            .map((event) => ({
              key: sumEventMetricKey(event.key),
              label: `${event.value} (Total $)`,
              hint: 'summed amount ($)',
            }))
        : [];

    const predictedMetrics =
      report.reportType === 'advertiser'
        ? []
        : eventOptions
            // Amount events included, unlike the attributed list: a predicted revenue or commission is
            // the point of predicting them, and they carry their own maturation cells like any event.
            // is_predicted, not is_attributed: enrolling an event in prediction is its own decision,
            // and an event withdrawn from it must stop being offered even though it stays attributed.
            .filter((event) => event.isPredicted)
            .map((event) => ({
              key: predictedMetricKey(event.key),
              label: `Predicted ${event.value}`,
              hint: 'expected final total once the window closes',
            }));

    /**
     * Impression family only: a last-touch row is an attribution row keyed on pixel_date, and
     * adte_conversion_v2 has no such event, so a conversion-date report would only ever read zero.
     *
     * Offered despite is_attributed being false — that flag gates derivation from the conversion
     * table, which these are not derived from. Their own label already carries "(Last Touch)", so
     * the "(Attributed)" suffix the normal list adds would be both wrong and doubled.
     */
    const lastTouchEventMetrics =
      report.reportType === 'advertiser'
        ? []
        : eventOptions
            .filter((event) => isLastTouchEvent(event.key))
            .map((event) => ({
              key: eventMetricKey(event.key),
              label: event.value,
              hint: 'whole credit to the closing impression',
            }));

    const calcMetricOptions = campaignCalculatedMetrics
      .filter((c) => c.target_mode === 'new' && c.is_active !== false)
      .filter(
        (c) =>
          calcMetricFamily(c) ===
          (report.reportType === 'advertiser' ? 'advertiser' : 'pixel')
      )
      .map((c) => ({
        key: c.key,
        label: customLabelMap.get(c.key) || c.display_label || c.key,
        hint: 'calculated column',
      }));

    return [
      ...baseMetrics,
      ...eventMetrics,
      ...lastTouchEventMetrics,
      ...matchedEventMetrics,
      ...sumEventMetrics,
      ...predictedMetrics,
      ...calcMetricOptions,
    ];
  }, [
    customLabelMap,
    eventOptions,
    report.reportType,
    campaignCalculatedMetrics,
    metadata.metrics,
  ]);

  const idsToMetrics = useMemo(
    () => new Map(metricsOptionsForComponent.map((item) => [item.key, item.label])),
    [metricsOptionsForComponent]
  );
  const selectedMetricOptions = useMemo(
    () =>
      report.metrics
        .map((metric) =>
          metricsOptionsForComponent.find((option) => option.key === metric.eventType)
        )
        .filter(Boolean) as IMetricOption[],
    [metricsOptionsForComponent, report.metrics]
  );

  /**
   * Shown but not chosen. A report of nothing but predictions asks the query for no columns, so the
   * request anchors itself to served impressions — this is that anchor made visible, greyed and
   * without a remove handle. Kept out of report.metrics deliberately: letting it look chosen would
   * let it be un-chosen, into a query with nothing to select.
   */
  const anchorMetricOption = useMemo(() => {
    const metrics = report.metrics ?? [];
    if (!metrics.some((m) => isPredictedMetricKey(m.eventType))) return null;
    if (metrics.some((m) => !isPredictedMetricKey(m.eventType))) return null;
    return metricsOptionsForComponent.find((o) => o.key === PREDICTION_ANCHOR_METRIC) ?? null;
  }, [report.metrics, metricsOptionsForComponent]);

  const shownMetricOptions = useMemo(
    () =>
      anchorMetricOption ? [...selectedMetricOptions, anchorMetricOption] : selectedMetricOptions,
    [selectedMetricOptions, anchorMetricOption]
  );

  const familyAnchorMetric =
    report.reportType === 'advertiser' ? 'advertiser_event_count' : 'matched_impression_count';
  const withFamilyAnchor = useCallback(
    (metrics: Array<{ eventType: string; label: string }>) => {
      const hasCoreFamilyMetric = metrics.some((m) =>
        report.reportType === 'advertiser'
          ? ADVERTISER_EVENT_METRICS.has(m.eventType)
          : PIXEL_DATE_METRICS.has(m.eventType)
      );
      if (hasCoreFamilyMetric || metrics.length === 0) {
        return metrics.length > 0
          ? metrics
          : [{ eventType: familyAnchorMetric, label: familyAnchorMetric }];
      }
      return [...metrics, { eventType: familyAnchorMetric, label: familyAnchorMetric }];
    },
    [familyAnchorMetric, report.reportType]
  );
  const metricsForDimensionFamily = useMemo(
    () => withFamilyAnchor(report.metrics),
    [report.metrics, withFamilyAnchor]
  );

  const dimensionOptions = useMemo(
    () =>
      getDimensionOptionsForMetrics(metricsForDimensionFamily, metadata.dimensions).map(
        (option) => ({
          key: option.key,
          value: customLabelMap.get(option.key) || option.value || option.key,
        })
      ),
    [customLabelMap, metricsForDimensionFamily, metadata.dimensions]
  );
  const idsToDimensions = useMemo(() => convertArrayToMap(dimensionOptions), [dimensionOptions]);
  const selectedDimensionOptions = useMemo(
    () =>
      report.dimensions
        .map((dimension) => dimensionOptions.find((option) => option.key === dimension.key))
        .filter(Boolean) as Array<{ key: string; value: string }>,
    [dimensionOptions, report.dimensions]
  );

  const filterDimensionOptions = useMemo(
    () =>
      getFilterDimensionOptionsForMetrics(
        metricsForDimensionFamily,
        report.dimensions,
        dimensionOptions,
        metadata.dimensions
      ),
    [dimensionOptions, metricsForDimensionFamily, report.dimensions, metadata.dimensions]
  );

  // Only metrics the report actually returns can be constrained; anything else is a 400.
  // Keys come from metricColumnName so they match the response columns exactly.
  // Predicted metrics are deliberately absent. They are merged into the rows after the query — after
  // grouping, after paging — so there is no SQL expression to compare against, and the backend refuses
  // a constraint it cannot bind to the same expression that produced the number.
  const constraintMetricOptions = useMemo(
    () =>
      report.metrics
        .filter((m) => !isPredictedMetricKey(m.eventType))
        .map((m) => ({
          key: metricColumnName(m.eventType),
          value: m.label,
        })),
    [report.metrics]
  );

  const dateDimensionLabel = getDateDimensionLabel(
    report.metrics,
    report.reportType,
    metadata.labels
  );

  const speed = useMemo(
    () => estimateReportSpeed(report, metadata.grains, metadata.metrics),
    [report, metadata.grains, metadata.metrics]
  );
  const speedHue = Math.round(130 * (1 - speed.score));

  const handleAggregationTimeChange = (event: any) => {
    setReport({ ...report, timeAggregation: event.target.value });
  };

  const handleAggregationPresetsChange = (event: any) => {
    const { startDate, endDate } = getTimeFromAggregationPresets(event.target.value);
    setReport({
      ...report,
      preset: event.target.value,
      startDate: startDate,
      endDate: endDate,
    });
  };

  const handleCampaignChange = (event: any) => {
    const picked: string[] = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    const ordered = campaignOptions.map((c) => c.id).filter((id) => picked.includes(id));
    // Calculated and predicted metrics need exactly one campaign, so several drop them.
    const calcKeys = new Set(campaignCalculatedMetrics.map((c) => c.key));
    const metrics =
      ordered.length > 1 ? report.metrics.filter((m) => !isPredictedMetricKey(m.eventType) && !calcKeys.has(m.eventType)) : report.metrics;
    setReport({ ...report, idCampaign: ordered.join(','), metrics });
  };

  const handleReportTypeChange = (event: any) => {
    const reportType = event.target.value as 'pixel' | 'advertiser';
    setReport({
      ...report,
      reportType,
      metrics: [],
      dimensions: [],
      filters: [],
    });
  };

  const getKeyValueArr = (chipsArray: string[], mapIdsToValue: Map<string, string>) => {
    if (!chipsArray) return [];
    return chipsArray.map((chip) => ({
      key: chip.toString(),
      value: mapIdsToValue.get(chip.toString()) || chip,
    }));
  };

  const handleDimensionsChips = (chipsArray: string[]) => {
    const chips = getKeyValueArr(chipsArray, idsToDimensions);
    const validFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        report.metrics,
        chips,
        dimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedFilters = report.filters.filter(
      (f) => !f.dimension || validFilterDimKeys.has(f.dimension)
    );
    setReport({ ...report, dimensions: chips, filters: prunedFilters });
  };

  const selectedDimensionKeys = useMemo(
    () => new Set(report.dimensions.map((d) => d.key)),
    [report.dimensions]
  );
  const fastDimensionChips = useMemo(
    () =>
      fastDimensionKeys(report, metadata.grains)
        .map((key) => dimensionOptions.find((option) => option.key === key))
        .filter(Boolean) as Array<{ key: string; value: string }>,
    [report, dimensionOptions, metadata.grains]
  );
  const toggleFastDimension = (key: string) => {
    const current = report.dimensions.map((d) => d.key);
    handleDimensionsChips(
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  };

  const handleMetricsChips = (chipsArray: string[]) => {
    const uniqueChips = Array.from(new Set(chipsArray));
    // Derived from the catalogue, with the same filter the picker builds its options from. Hardcoded,
    // this list silently dropped any core metric the backend added: the option appeared, clicking it
    // did nothing, and nothing anywhere reported a reason.
    const family = report.reportType === 'advertiser' ? 'conversion' : 'impression';
    const allowedCoreMetrics = new Set(
      metadata.metrics
        .filter((m) => m.selectable !== false && (m.reports ?? []).includes(family))
        .map((m) => m.key)
    );

    // 'new' calc-metric keys are bare slugs, not event:: or core keys — allow them through
    // or selecting a calc metric gets silently dropped.
    const calcKeys = new Set(
      campaignCalculatedMetrics
        .filter((c) => c.target_mode === 'new' && c.is_active !== false)
        .map((c) => c.key)
    );
    const resolved = uniqueChips.filter(
      (eventType) =>
        isEventMetricKey(eventType) ||
        isMatchedEventMetricKey(eventType) ||
        isSumEventMetricKey(eventType) ||
        isPredictedMetricKey(eventType) ||
        allowedCoreMetrics.has(eventType) ||
        calcKeys.has(eventType)
    );

    const metricsToStore = resolved.map((eventType) => ({
      eventType,
      label: idsToMetrics.get(eventType) || eventType,
    }));

    const resolvedDimensionOptions = getDimensionOptionsForMetrics(
      withFamilyAnchor(metricsToStore),
      metadata.dimensions
    );
    const validDimKeys = new Set(resolvedDimensionOptions.map((o) => o.key));
    const validFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        metricsToStore,
        report.dimensions,
        resolvedDimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedDimensions = report.dimensions.filter((d) => validDimKeys.has(d.key));
    const prunedFilterDimKeys = new Set(
      getFilterDimensionOptionsForMetrics(
        metricsToStore,
        prunedDimensions,
        resolvedDimensionOptions,
        metadata.dimensions
      ).map((o) => o.key)
    );
    const prunedFilters = report.filters.filter(
      (f) =>
        !f.dimension ||
        (validFilterDimKeys.has(f.dimension) && prunedFilterDimKeys.has(f.dimension))
    );

    const removedKeys = Array.from(
      new Set([
        ...report.dimensions.filter((d) => !validDimKeys.has(d.key)).map((d) => d.key),
        ...report.filters
          .filter(
            (f) =>
              f.dimension &&
              !(validFilterDimKeys.has(f.dimension) && prunedFilterDimKeys.has(f.dimension))
          )
          .map((f) => f.dimension),
      ])
    );
    if (removedKeys.length > 0) {
      const removedLabels = removedKeys.map((key) => idsToDimensions.get(key) || key).join(', ');
      dispatch(setAlert({
        msg: `Removed ${removedLabels} — not available for the selected metric(s).`,
        type: AlertTypes.WARNING,
        d: dispatch,
      }));
    }

    setReport({
      ...report,
      metrics: metricsToStore,
      dimensions: prunedDimensions,
      filters: prunedFilters,
    });
  };

  const dragSourceIndexRef = useRef<number | null>(null);

  const reorderItems = useCallback(<T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= items.length ||
      toIndex >= items.length
    ) {
      return items;
    }
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  }, []);

  const reorderDimensions = useCallback(
    (fromIndex: number, toIndex: number) => {
      setReport({ ...report, dimensions: reorderItems(report.dimensions, fromIndex, toIndex) });
    },
    [report, reorderItems, setReport]
  );

  const reorderMetrics = useCallback(
    (fromIndex: number, toIndex: number) => {
      setReport({ ...report, metrics: reorderItems(report.metrics, fromIndex, toIndex) });
    },
    [report, reorderItems, setReport]
  );

  const getChipDragProps = useCallback(
    (index: number, onReorder: (fromIndex: number, toIndex: number) => void) => ({
      draggable: true,

      onMouseDown: (e: React.MouseEvent) => {
        e.stopPropagation();
      },
      onDragStart: (e: React.DragEvent) => {
        dragSourceIndexRef.current = index;
        e.dataTransfer.effectAllowed = 'move';

        e.dataTransfer.setData('text/plain', String(index));
      },
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const fromIndex = dragSourceIndexRef.current;
        if (fromIndex !== null && fromIndex !== index) onReorder(fromIndex, index);
        dragSourceIndexRef.current = null;
      },
      onDragEnd: () => {
        dragSourceIndexRef.current = null;
      },
      sx: { cursor: 'grab', '&:active': { cursor: 'grabbing' } },
    }),
    []
  );

  const handleFilterChange = (id: string, field: string, value: any) => {
    setReport({
      ...report,
      filters: report.filters.map((f) => (f.id === id ? { ...f, [field]: value } : f)),
    });
  };

  const handleAddFilter = () => {
    setReport({
      ...report,
      filters: [
        ...report.filters,
        { id: createFilterId(), dimension: '', action: 'include', specTarget: [] },
      ],
    });
  };

  const handleDeleteFilter = (id: string) => {
    setReport({ ...report, filters: report.filters.filter((f) => f.id !== id) });
  };

  const handleConstraintChange = (id: string, field: string, value: any) => {
    setReport({
      ...report,
      constraints: (report.constraints || []).map((c) =>
        c.id === id ? { ...c, [field]: value } : c
      ),
    });
  };

  const handleAddConstraint = () => {
    setReport({
      ...report,
      constraints: [
        ...(report.constraints || []),
        { id: createConstraintId(), metric: '', operator: 'gt', value: '' },
      ],
    });
  };

  const handleDeleteConstraint = (id: string) => {
    setReport({
      ...report,
      constraints: (report.constraints || []).filter((c) => c.id !== id),
    });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setReport({ ...report, [name]: value });
  };

  const handleRelativePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReport({ ...report, isRelativePeriod: event.target.checked });
  };

  return (
    <div>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {report._id ? 'Update Report' : 'Report Builder'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Configure dimensions, metrics, and filters to generate conversion reports.
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', mt: 0.5, maxWidth: 760, mx: 'auto' }}
        >
          {`Date range applies to ${dateDimensionLabel}. `}
          Impression-date and conversion-date reports are separate modes. Event
          metrics are selected inside Metrics and are rendered as columns (not row
          dimensions).{' '}
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={() => setTabIndex(2)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') setTabIndex(2);
            }}
            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
          >
            How metrics work
          </Box>
        </Typography>
      </Box>

      <Grid
        container
        direction={'row'}
        justifyContent={'flex-start'}
        alignItems={'start'}
        columnGap={'10px'}
        rowGap={'5px'}
      >
        {Boolean(showReportSettings || report._id) && (
          <Grid
            container
            direction={'column'}
            justifyContent={'flex-start'}
            alignItems={'start'}
            rowGap={'10px'}
          >
            <FormHelperText sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              Report Settings
            </FormHelperText>
            <Grid
              container
              direction={'row'}
              justifyContent={'flex-start'}
              alignItems={'start'}
              columnGap={'10px'}
            >
              <Grid size={{ lg: 2 }}>
                <FormHelperText>Template Name</FormHelperText>
                <TextField
                  fullWidth
                  value={report.name}
                  onChange={onChange}
                  size="small"
                  variant="outlined"
                  name="name"
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                />
              </Grid>
              <Grid size={{ lg: 2 }}>
                <FormHelperText>Created By</FormHelperText>
                <TextField
                  disabled={!!report._id}
                  fullWidth
                  value={report.createdBy}
                  onChange={onChange}
                  size="small"
                  variant="outlined"
                  name="createdBy"
                  error={!!validationErrors.createdBy}
                  helperText={validationErrors.createdBy}
                />
              </Grid>
              <Grid size={{ lg: 2 }}>
                <br />
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={report.isRelativePeriod}
                        onChange={handleRelativePeriodChange}
                      />
                    }
                    label={report.isRelativePeriod ? 'Relative Period' : 'Fixed Period'}
                  />
                </FormGroup>
              </Grid>
            </Grid>
            <Grid
              container
              direction={'row'}
              justifyContent={'flex-start'}
              alignItems={'start'}
              columnGap={'10px'}
              paddingBottom={'40px'}
            >
              <Grid size={{ lg: 4 }}>
                <FormHelperText>Description</FormHelperText>
                <TextField
                  fullWidth
                  value={report.description}
                  onChange={onChange}
                  size="medium"
                  variant="outlined"
                  name="description"
                  error={!!validationErrors.description}
                  helperText={validationErrors.description}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid size={{ lg: 2 }}>
          <FormHelperText>Presets</FormHelperText>
          <Select
            variant={'outlined'}
            value={report.preset}
            label="presets"
            onChange={handleAggregationPresetsChange}
            fullWidth={true}
          >
            {timePresets.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid size={{ lg: 4, md: 4, sm: 4, xs: 4 }}>
          <Box sx={{ position: 'relative' }}>
            {/* Pinned above the label so the pickers keep their place instead of being shoved down. */}
            {rangeError && (
              <FormHelperText
                error
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  mx: 0,
                  mb: 0.25,
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {rangeError}
              </FormHelperText>
            )}
            <FormHelperText error={!!rangeError} sx={{ textAlign: 'center' }}>
              Time Range
            </FormHelperText>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" alignItems="center" columnGap={1}>
              <DatePicker
                label="Start Date"
                value={report.startDate ? dayjs(report.startDate).toDate() : null}
                format="dd/MM/yyyy"
                onChange={(newValue: any) => {
                  setReport({
                    ...report,
                    startDate: newValue ? dayjs(newValue).format('YYYY-MM-DD') : null,
                    preset: 'choose time preset',
                  });
                }}
                maxDate={dayjs().toDate()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!validationErrors.startDate || !!rangeError,
                    helperText: validationErrors.startDate,
                  },
                  actionBar: { actions: ['today'] },
                }}
              />
              <DatePicker
                label="End Date"
                value={report.endDate ? dayjs(report.endDate).toDate() : null}
                format="dd/MM/yyyy"
                onChange={(newValue: any) => {
                  setReport({
                    ...report,
                    endDate: newValue ? dayjs(newValue).format('YYYY-MM-DD') : null,
                    preset: 'choose time preset',
                  });
                }}
                maxDate={dayjs().toDate()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!validationErrors.endDate || !!rangeError,
                    helperText: validationErrors.endDate,
                  },
                  actionBar: { actions: ['today'] },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Grid>

        <Grid size={{ lg: 2 }}>
          <FormHelperText>Aggregation</FormHelperText>
          <Select
            variant={'outlined'}
            value={report.timeAggregation}
            label="aggregation period"
            onChange={handleAggregationTimeChange}
            fullWidth={true}
          >
            {timeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid size={{ lg: 2 }}>
          <FormHelperText>Report Type</FormHelperText>
          <Select
            variant={'outlined'}
            value={report.reportType}
            label="report type"
            onChange={handleReportTypeChange}
            fullWidth={true}
          >
            <MenuItem value="pixel">Impression Date Report</MenuItem>
            <MenuItem value="advertiser">Conversion Date Report</MenuItem>
          </Select>
        </Grid>

        <Grid size={{ lg: 2 }}>
          <FormHelperText error={!!validationErrors.idCampaign}>Campaign</FormHelperText>
          <Select
            variant={'outlined'}
            multiple
            value={selectedCampaigns}
            label="campaign"
            onChange={handleCampaignChange}
            fullWidth={true}
            displayEmpty
            error={!!validationErrors.idCampaign}
            renderValue={(ids: string[]) =>
              ids.length === 0
                ? campaignOptions.length === 0
                  ? 'No campaigns available'
                  : 'Choose campaigns'
                : ids.map((id) => campaignOptions.find((c) => c.id === id)?.name ?? id).join(', ')
            }
          >
            {campaignOptions.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Checkbox size="small" checked={selectedCampaigns.includes(c.id)} sx={{ p: 0.5, mr: 1 }} />
                {c.name}
              </MenuItem>
            ))}
          </Select>
          {validationErrors.idCampaign && (
            <FormHelperText error>{validationErrors.idCampaign}</FormHelperText>
          )}
          {multiCampaign && !validationErrors.idCampaign && (
            <FormHelperText>
              {report.dimensions.some((d) => d.key === 'id_campaign') ? 'Rows per campaign.' : 'Summed; add Campaign to split.'} No predicted or calculated metrics.
            </FormHelperText>
          )}
        </Grid>

        <Grid size={{ lg: 8 }}>
          <FormHelperText sx={{ visibility: 'hidden' }}>Dimensions and Metrics</FormHelperText>
          <Box display="flex" justifyContent="space-between" columnGap={1}>
            <Box sx={{ width: '100%' }}>
              <Autocomplete
                multiple
                disableCloseOnSelect={false}
                filterSelectedOptions
                options={dimensionOptions}
                value={selectedDimensionOptions}
                isOptionEqualToValue={(option: any, value: any) => option.key === value.key}
                getOptionLabel={(option: any) => option.value}
                renderOption={(props: any, option: any) => {

                  const { key: _key, ...optionProps } = props;
                  return (
                    <li {...optionProps} key={option.key}>
                      {option.value}
                    </li>
                  );
                }}
                onChange={(_event: any, options: any[]) =>
                  handleDimensionsChips(options.map((option) => option.key))
                }
                renderTags={(value: any[], getTagProps: any) =>
                  value.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      {...getChipDragProps(index, reorderDimensions)}
                      key={option.key || option.value || index}
                      label={option.value}
                      color="primary"
                      variant="filled"
                    />
                  ))
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label="Dimensions"
                    name="Dimensions"
                    error={!!validationErrors.dimensions}
                    helperText={validationErrors.dimensions}
                  />
                )}
              />
              {fastDimensionChips.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    title="These dimensions stay on the fast pre-aggregated data; others fall back to a slower raw scan."
                    sx={{ fontSize: 12, fontWeight: 600, mr: 0.5, cursor: 'default' }}
                  >
                    ⚡ Fast
                  </Box>
                  {fastDimensionChips.map((chip) => {
                    const selected = selectedDimensionKeys.has(chip.key);
                    return (
                      <Chip
                        key={chip.key}
                        label={chip.value}
                        size="small"
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => toggleFastDimension(chip.key)}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
            <Box sx={{ width: '100%' }}>
              <Autocomplete
                multiple
                disableCloseOnSelect={false}
                filterSelectedOptions
                options={metricsOptionsForComponent}
                value={shownMetricOptions}
                isOptionEqualToValue={(option: IMetricOption, value: IMetricOption) =>
                  option.key === value.key
                }
                getOptionLabel={(option: IMetricOption) => option.label}
                onChange={(_event: any, options: IMetricOption[]) =>
                  handleMetricsChips(options.map((option) => option.key))
                }
                renderTags={(value: IMetricOption[], getTagProps: any) =>
                  value.map((option: IMetricOption, index: number) => {
                    const isAnchor = option.key === anchorMetricOption?.key;
                    const chip = (
                      <Chip
                        {...getTagProps({ index })}
                        {...(isAnchor ? {} : getChipDragProps(index, reorderMetrics))}
                        key={option.key || index}
                        label={option.label}
                        color={isAnchor ? 'default' : 'primary'}
                        variant="filled"
                        sx={
                          isAnchor
                            ? { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
                            : undefined
                        }
                        // No handle rather than a dead one: the anchor goes away by selecting another
                        // metric, and a cross that refuses to work invites the opposite conclusion.
                        onDelete={isAnchor ? undefined : getTagProps({ index }).onDelete}
                      />
                    );
                    if (!isAnchor) return chip;
                    return (
                      <Tooltip
                        key={option.key || index}
                        title="Added automatically so the predictions have something to be read against. Select another metric and it becomes a normal column you can remove."
                      >
                        <span>{chip}</span>
                      </Tooltip>
                    );
                  })
                }
                renderOption={(props: any, option: IMetricOption) => (
                  <li {...props}>
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ textAlign: 'left', color: 'text.primary' }}>
                        {option.label}
                      </Typography>
                      {option.hint && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', textAlign: 'left', fontSize: '0.72rem' }}
                        >
                          - {option.hint}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label="Metrics"
                    name="Metrics"
                    error={!!validationErrors.metrics}
                    helperText={validationErrors.metrics}
                  />
                )}
              />
            </Box>
          </Box>
        </Grid>

        <Grid size={{ lg: 12 }} sx={{ mt: 1 }}>
          <Filters
            dimensionsOptions={filterDimensionOptions}
            filtersData={report.filters}
            onChange={handleFilterChange}
            onAddFilter={handleAddFilter}
            onDeleteFilter={handleDeleteFilter}
          />
          {report.filters.length === 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleAddFilter}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              + Add Filter
            </Button>
          )}
        </Grid>

        <Grid size={{ lg: 12 }} sx={{ mt: 1 }}>
          <Constraints
            metricOptions={constraintMetricOptions}
            constraintsData={report.constraints || []}
            onChange={handleConstraintChange}
            onAddConstraint={handleAddConstraint}
            onDeleteConstraint={handleDeleteConstraint}
          />
          {(report.constraints || []).length === 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={constraintMetricOptions.length === 0}
              onClick={handleAddConstraint}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              + Add Constraint
            </Button>
          )}
        </Grid>

        <Grid size={{ lg: 12 }} style={{ marginTop: '16px' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="contained"
              onClick={runReport}
              color="primary"
              size="large"
              disabled={isFetchingReportData || !!rangeError}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4 }}
            >
              Run Report
            </Button>
            <Box
              title={
                speed.level === 'fast'
                  ? 'Reads pre-aggregated data — should return quickly.'
                  : `Estimated runtime. Some selected dimensions read raw/detailed data; use the ⚡ Fast dimensions to speed it up${
                      speed.level === 'slow'
                        ? ', and please stay on this page until it finishes.'
                        : '.'
                    }`
              }
              sx={{
                ml: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                minHeight: 42,
                borderRadius: 2,
                border: '1px solid',
                borderColor: `hsl(${speedHue}, 70%, 45%)`,
                backgroundColor: `hsla(${speedHue}, 70%, 45%, 0.12)`,
                color: `hsl(${speedHue}, 75%, 62%)`,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'default',
                whiteSpace: 'nowrap',
              }}
            >
              <span>
                {speed.level === 'fast' ? '⚡' : speed.level === 'moderate' ? '🚶' : '🐢'}
              </span>
              <span>{speed.label}</span>
              <Box component="span" sx={{ fontWeight: 400, opacity: 0.85 }}>
                {speed.estimate}
              </Box>
            </Box>
            {isFetchingReportData && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={cancelQuery}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Grid>

        <Grid size={{ lg: 12 }}>
          {!reportData && isFetchingReportData && (
            <Box display="flex" alignItems="center" justifyContent="center" padding={4} gap={2}>
              <CircularProgress size={28} />
              <Typography variant="body1" color="textSecondary">
                Running query...{' '}
                {queryElapsedMs > 0 && `${Math.round(queryElapsedMs / 1000)}s elapsed`}
              </Typography>
            </Box>
          )}
          {reportData && reportData.length > 0 && (
            <ReportChart
              rows={chartRows ?? reportData}
              report={report}
              columnLabels={new Map(columns.map((c) => [c.name, c.label]))}
              predictionSeries={predictionSeries}
              predictionError={predictionError}
            />
          )}
          {reportData && reportData.length > 0 && (
            <ReportTable
              data={reportData}
              columns={columns}
              loading={isFetchingReportData}
              refreshFunc={runReport}
            />
          )}
          {/*
            * A failed report also lands here with zero rows, so the empty-state text
            * is suppressed when there is a reason to show instead - otherwise a query
            * the backend refused to run reads as one that ran and found nothing. These
            * two used to render together, claiming both at once.
            */}
          {!isFetchingReportData && reportData?.length === 0 && !hasReportFailure && (
            <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
              No data found for the selected criteria.
            </Typography>
          )}
          {hasReportFailure && (
            <MuiAlert severity="error" sx={{ my: 2 }}>
              <AlertTitle>This report could not be run</AlertTitle>
              {/* Prefer the backend's own words; it names the exact clash and the fix. */}
              {reportError ?? 'Query failed. Please try again or adjust your parameters.'}
            </MuiAlert>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default ReportBuilderView;
